// ✅ Trekkin.app — exportGPX.js v1.0.0
// 🔹 Converte um percurso gravado (array de {lat, lon, time}) em ficheiro GPX
// 🔹 Opcionalmente inclui metadados (nome, data)
// 🔹 Retorna string GPX pronta a guardar ou descarregar

import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

export function exportGPX(recordedTrack, meta = {}) {
  if (!recordedTrack?.length) {
    console.warn("⚠️ Nenhum ponto gravado para exportar GPX");
    return null;
  }

  const { name = "Trekkin Track", date = new Date().toISOString() } = meta;

  const gpxHeader = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Trekkin.app" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${name}</name>
    <time>${date}</time>
  </metadata>
  <trk>
    <name>${name}</name>
    <trkseg>`;

  let lastEle = null;
  const gpxPoints = recordedTrack
    .map((p) => {
      const raw =
        Number(p?.time) ||
        Number(p?.ts) ||
        Number(p?.timestamp) ||
        Number(p?.timeMs) ||
        0;
      const ts = raw ? (raw < 1e12 ? raw * 1000 : raw) : 0;

    const rawEle = Number.isFinite(p?.ele)
      ? Number(p.ele)
      : Number.isFinite(p?.alt)
      ? Number(p.alt)
      : Number.isFinite(p?.altitude)
      ? Number(p.altitude)
      : null;
    if (Number.isFinite(rawEle)) lastEle = rawEle;
    const eleVal = Number.isFinite(rawEle)
      ? rawEle
      : Number.isFinite(lastEle)
      ? lastEle
      : 0;
    const ele = `<ele>${Number(eleVal).toFixed(1)}</ele>`;
      const iso = ts ? new Date(ts).toISOString() : new Date().toISOString();
      return `\n      <trkpt lat="${p.lat.toFixed(6)}" lon="${p.lon.toFixed(
        6
      )}">${ele}<time>${iso}</time></trkpt>`;
    })
    .join("");

  const gpxFooter = `
    </trkseg>
  </trk>
</gpx>`;

  const gpxContent = gpxHeader + gpxPoints + gpxFooter;
  console.log(`📦 GPX gerado (${recordedTrack.length} pontos)`);

  return gpxContent;
}

// 💾 (Opcional) Função auxiliar para descarregar o ficheiro localmente
export function downloadGPX(recordedTrack, meta) {
  const gpx = exportGPX(recordedTrack, meta);
  if (!gpx) return;

  const blob = new Blob([gpx], { type: "application/gpx+xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${(meta?.name || "TrekkinTrack").replace(/\s+/g, "_")}.gpx`;
  link.click();
  URL.revokeObjectURL(url);
}

export function buildNativeGpxSharePayload(uri, filename) {
  return {
    title: filename,
    files: [uri],
    dialogTitle: "Exportar GPX",
  };
}

export async function shareGpxText(gpxText, filename = "TrekkinTrack.gpx") {
  const text = String(gpxText || "").trim();
  if (!text) return false;
  const safeFilename = String(filename || "TrekkinTrack.gpx").endsWith(".gpx")
    ? String(filename || "TrekkinTrack.gpx")
    : `${filename}.gpx`;

  const iosBridge = window?.webkit?.messageHandlers?.nativeBridge;
  if (typeof iosBridge?.postMessage === "function") {
    iosBridge.postMessage({ action: "downloadGPX", filename: safeFilename, gpx: text });
    return true;
  }

  if (Capacitor.isNativePlatform()) {
    const data = btoa(unescape(encodeURIComponent(text)));
    const result = await Filesystem.writeFile({
      path: `exports/${safeFilename}`,
      data,
      directory: Directory.Cache,
      recursive: true,
    });
    if (!result?.uri) throw new Error("O dispositivo não devolveu o URI do ficheiro GPX");
    await Share.share(buildNativeGpxSharePayload(result.uri, safeFilename));
    return true;
  }

  const blob = new Blob([text], { type: "application/gpx+xml" });
  const file = new File([blob], safeFilename, { type: "application/gpx+xml" });
  if (navigator?.share && navigator?.canShare?.({ files: [file] })) {
    await navigator.share({ title: safeFilename, files: [file] });
    return true;
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeFilename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 100);
  return true;
}
